import { useState } from 'react';
import { ProductDiscoveryLoading } from './ProductDiscoveryLoading';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, ShoppingBag, X, Loader2, Check, Shirt, RefreshCw, Leaf, Eye, ChevronRight } from 'lucide-react';
import wLogo from '@/assets/want-w-icon.svg';
import { cn } from '@/lib/utils';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { SourceIcon } from '@/components/products/SourceIcon';
import { sanitizeExternalUrl } from '@/utils/sanitizeExternalUrl'; // kept for potential future use
import { useSavedItems } from '@/hooks/useSavedItems';
import { useAuth } from '@/hooks/useAuth';
import { useRegionalSettings } from '@/hooks/useRegionalSettings';
import { useHaptics } from '@/hooks/useHaptics';
import { CategoryShopSheet } from '@/components/products/CategoryShopSheet';
import { ProductPreviewLightbox } from '@/components/ui/ProductPreviewLightbox';
import { SuggestionFriendPicker } from '@/components/share/SuggestionFriendPicker';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { SaveButton } from '@/components/ui/SaveButton';

interface ShoppableProduct {
  id: string;
  name: string;
  category: string;
  price: number | null;
  currency: string;
  formattedPrice: string;
  image: string;
  url: string;
  store: string;
  originalProduct: string;
  isSecondhand?: boolean;
  matchedBrand?: string;
}

interface ProductCategory {
  category: string;
  displayName: string;
  products: ShoppableProduct[];
}

interface ProductDiscoveryResultsProps {
  imageTitle: string;
  categories: ProductCategory[];
  totalProducts: number;
  isLoading?: boolean;
  sourceImage?: string;
  onClose?: () => void;
  brandHints?: string[];
}

export function ProductDiscoveryResults({
  imageTitle,
  categories,
  totalProducts,
  isLoading = false,
  sourceImage,
  onClose,
  brandHints = [],
}: ProductDiscoveryResultsProps) {
  const { user } = useAuth();
  const { createItem } = useSavedItems();
  const { formatPrice } = useRegionalSettings();
  const { trigger: triggerHaptic } = useHaptics();
  const navigate = useNavigate();
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<{ label: string; query: string; imageUrl?: string } | null>(null);
  const [previewProduct, setPreviewProduct] = useState<ShoppableProduct | null>(null);
  const [showFriendPicker, setShowFriendPicker] = useState(false);

  // Brand matching helper
  const normalizedHints = brandHints.map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
  
  const getMatchedBrand = (product: ShoppableProduct): string | null => {
    if (normalizedHints.length === 0) return null;
    const searchText = `${product.name} ${product.store} ${product.url}`.toLowerCase().replace(/[^a-z0-9 ]/g, '');
    for (let i = 0; i < normalizedHints.length; i++) {
      if (normalizedHints[i].length >= 2 && searchText.includes(normalizedHints[i])) {
        return brandHints[i]; // Return original casing
      }
    }
    return null;
  };

  const hasBrandHints = brandHints.length > 0;
  const hasAnyBrandMatch = hasBrandHints && categories.some(cat =>
    cat.products.some(p => getMatchedBrand(p) !== null)
  );

  const handleSeeMore = (category: ProductCategory) => {
    const isVisualCategory = category.category === 'visual';
    const leadProduct = category.products[0];

    // For Exact Matches, anchor follow-up search on the product image (Lens) for relevance.
    const sampleNames = category.products.slice(0, 2).map(p => p.originalProduct || p.name).join(' ');
    const searchQuery = isVisualCategory
      ? (leadProduct?.originalProduct || leadProduct?.name || imageTitle)
      : `${category.displayName} ${sampleNames}`.trim();

    setSelectedCategory({
      label: category.displayName,
      query: searchQuery,
      imageUrl: isVisualCategory ? leadProduct?.image : undefined,
    });
  };

  const handleSaveProduct = async (product: ShoppableProduct, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Sign in to save items');
      return;
    }
    
    if (savedIds.has(product.id) || savingIds.has(product.id)) return;

    triggerHaptic('success');
    // Optimistically mark as saved immediately for instant UI feedback
    setSavedIds(prev => new Set(prev).add(product.id));
    setSavingIds(prev => new Set(prev).add(product.id));

    try {
      await createItem.mutateAsync({
        name: product.name,
        url: product.url,
        image_url: product.image,
        price: product.price ?? undefined,
        currency: product.currency,
        store: product.store,
        source_type: 'url',
        save_source: 'find_similar',
      });
      // Haptic already provides feedback
    } catch (error) {
      console.error('Error saving product:', error);
      // Roll back optimistic update
      setSavedIds(prev => { const next = new Set(prev); next.delete(product.id); return next; });
      toast.error('Could not save item');
    } finally {
      setSavingIds(prev => {
        const next = new Set(prev);
        next.delete(product.id);
        return next;
      });
    }
  };

  const handleTryOn = (product: ShoppableProduct, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    triggerHaptic('light');
    navigate('/try-on', {
      state: {
        productImage: product.image,
        productName: product.name,
        productPrice: product.formattedPrice,
      }
    });
  };

  const handleOpenProduct = (product: ShoppableProduct) => {
    setPreviewProduct(product);
  };

  if (isLoading) {
    return <ProductDiscoveryLoading />;
  }

  if (categories.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="py-8 text-center"
      >
        <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <p className="font-medium text-foreground">No products found</p>
        <p className="text-sm text-muted-foreground mt-1">
          Try a clearer image with visible clothing or accessories
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-5"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        {sourceImage && (
          <img
            src={sourceImage}
            alt="Source"
            className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground line-clamp-1">{imageTitle}</h3>
          <p className="text-sm text-muted-foreground">
            {totalProducts} shoppable {totalProducts === 1 ? 'item' : 'items'} found
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Brand hints chips */}
      {hasBrandHints && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-muted-foreground">Brands mentioned:</span>
            {brandHints.map((brand) => (
              <span
                key={brand}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium"
              >
                {brand}
              </span>
            ))}
          </div>
          {!hasAnyBrandMatch && totalProducts > 0 && (
            <p className="text-xs text-muted-foreground italic">
              No exact brand matches found yet — showing closest alternatives.
            </p>
          )}
        </div>
      )}

      {/* Categories */}
      <div className="space-y-6">
        {categories.map((category, catIdx) => (
          <motion.div
            key={category.category}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: catIdx * 0.1 }}
          >
            {/* Category Header */}
            <div className="flex items-baseline justify-between mb-3">
              <div className="flex items-center gap-2">
                {category.category === 'visual' && (
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                    <Eye className="w-3 h-3 text-primary" />
                  </div>
                )}
                <span className={cn(
                  "text-sm font-semibold",
                  category.category === 'visual' ? "text-primary" : "text-foreground"
                )}>
                  {category.displayName}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({category.products.length})
                </span>
                {category.category === 'visual' && (
                  <span className="text-[10px] text-muted-foreground bg-primary/10 px-1.5 py-0.5 rounded-full">
                    Visual match
                  </span>
                )}
              </div>
              <button
                onClick={() => handleSeeMore(category)}
                className="text-xs font-medium text-primary flex items-center gap-0.5 active:opacity-70 transition-opacity whitespace-nowrap"
              >
                See more <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-2 gap-3">
              {category.products.map((product, prodIdx) => (
                <motion.div
                  key={product.id}
                  onClick={() => handleOpenProduct(product)}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: catIdx * 0.1 + prodIdx * 0.05 }}
                  className="block group cursor-pointer"
                >
                  {/* Image container with overlay badges */}
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-surface-2">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-active:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    
                    {/* Action buttons - top right vertical stack */}
                    <div className="absolute top-2 right-2 flex flex-col gap-1.5">
                      {/* Save button */}
                      <SaveButton
                        saved={savedIds.has(product.id)}
                        onToggle={() => handleSaveProduct(product, { preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent)}
                        size="sm"
                        variant="overlay"
                        disabled={savingIds.has(product.id)}
                      />
                      
                      {/* Try On button */}
                      {product.image && (
                        <button
                          onClick={(e) => handleTryOn(product, e)}
                          className="w-8 h-8 rounded-full bg-black/20 backdrop-blur-md text-white/80 border border-white/10 flex items-center justify-center active:scale-95 transition-all shadow-sm"
                        >
                          <Shirt className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                                    {/* Price badge - bottom left */}
                                    {(() => {
                                      const localized = formatPrice(product.price, product.currency);
                                      const display = localized || product.formattedPrice;
                                      return (
                                        <div className="absolute bottom-2 left-2 flex items-center gap-1">
                                          {display && (
                                            <div className="bg-background/90 backdrop-blur-sm rounded-full px-2 py-0.5 shadow-sm leading-[115%]">
                                              <span className="text-xs font-semibold text-foreground">
                                                {display}
                                              </span>
                                            </div>
                                          )}
                                          {/* Pre-loved badge for secondhand items */}
                                          {product.isSecondhand && (
                                            <div className="bg-emerald-500/90 backdrop-blur-sm rounded-full px-1.5 py-0.5 shadow-sm flex items-center gap-0.5">
                                              <Leaf className="w-3 h-3 text-white" />
                                              <span className="text-[10px] text-white font-medium">Pre-loved</span>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    
                                    {/* Store badge - bottom right */}
                                    <div className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm">
                                      <span className="text-[10px] text-white font-medium truncate max-w-[60px] block">
                                        {product.store}
                                      </span>
                                    </div>
                  </div>
                  
                  {/* Product name + brand match below image */}
                  {(() => {
                    const matched = getMatchedBrand(product);
                    return (
                      <div className="mt-2">
                        {matched && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium mb-1">
                            <Check className="w-2.5 h-2.5" />
                            Matches {matched}
                          </span>
                        )}
                        <p className="text-xs font-medium line-clamp-2 text-foreground leading-tight">
                          {product.name}
                        </p>
                      </div>
                    );
                  })()}
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Category Shop Sheet for expanded search */}
      <CategoryShopSheet
        open={!!selectedCategory}
        onOpenChange={(open) => !open && setSelectedCategory(null)}
        categoryLabel={selectedCategory?.label || ''}
        searchQuery={selectedCategory?.query || ''}
        imageUrl={selectedCategory?.imageUrl}
      />

      {/* Product Preview Lightbox */}
      <ProductPreviewLightbox
        open={!!previewProduct}
        onClose={() => setPreviewProduct(null)}
        product={previewProduct ? {
          name: previewProduct.name,
          image: previewProduct.image,
          url: previewProduct.url,
          price: previewProduct.price,
          currency: previewProduct.currency,
          store: previewProduct.store,
        } : null}
        onSave={() => previewProduct && handleSaveProduct(previewProduct, { preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent)}
        isSaved={previewProduct ? savedIds.has(previewProduct.id) : false}
        onTryOn={() => {
          if (previewProduct) {
            setPreviewProduct(null);
            navigate('/try-on', { state: { productImage: previewProduct.image, productName: previewProduct.name } });
          }
        }}
        onShare={() => setShowFriendPicker(true)}
        onViewDetails={async () => {
          if (!previewProduct) return;
          try {
            const saved = await createItem.mutateAsync({
              name: previewProduct.name,
              url: previewProduct.url,
              image_url: previewProduct.image,
              price: previewProduct.price ?? undefined,
              currency: previewProduct.currency,
              store: previewProduct.store,
              source_type: 'url',
              save_source: 'find_similar',
            });
            setSavedIds(prev => new Set(prev).add(previewProduct.id));
            setPreviewProduct(null);
            navigate(`/saved/${saved.id}`);
          } catch {
            toast.error('Could not load details');
          }
        }}
        formatPrice={formatPrice}
      />

      {/* Suggestion Friend Picker */}
      <SuggestionFriendPicker
        open={showFriendPicker}
        onOpenChange={setShowFriendPicker}
        product={previewProduct ? {
          name: previewProduct.name,
          url: previewProduct.url,
          image_url: previewProduct.image,
          price: previewProduct.price,
          currency: previewProduct.currency,
          store: previewProduct.store,
        } : null}
      />
    </motion.div>
  );
}
